import { getStoredCompanyKey } from '@/services/api/erpat-client';

/**
 * Local "planned for today" tracking for tasks.
 *
 * There is no `myDay` flag anywhere in ERPat's todos API — My Day membership
 * in this app is entirely derived from due date + status. That leaves no way
 * to pull a task due next week onto today's plate without one of two things:
 * actually changing its real due_date (a genuine, visible-in-ERPat's-own-web
 * mutation, with no way to annotate "this was just today's plan"), or
 * tracking the intent locally. This is the latter — modeled on note-edits.ts,
 * with one change: it stores an add TIMESTAMP per id, not a bare id set, so
 * membership self-expires at the next calendar day instead of persisting
 * forever once a task is pulled in.
 *
 * Same two limits as note-edits.ts, both inherent to being client-only:
 *  - Per device. Planning a task from a second device doesn't show up here.
 *  - Invisible to ERPat's own web/other clients — this never leaves the device.
 *
 * Scoped by company key so switching tenants can't leak one tenant's plan
 * into another's task list.
 */

const STORAGE_PREFIX = 'syntry_todo_planned_today:';

function storageKey(): string {
  const tenant = (getStoredCompanyKey() ?? '').trim().toLowerCase();
  return `${STORAGE_PREFIX}${tenant}`;
}

function readMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof at === 'number' && Number.isFinite(at)) out[id] = at;
    }
    return out;
  } catch {
    return {};
  }
}

function startOfDay(ms: number): number {
  return new Date(ms).setHours(0, 0, 0, 0);
}

/** True only if the task was planned on the SAME calendar day as `now`. */
export function isPlannedToday(taskId: string, now: number = Date.now()): boolean {
  const at = readMap()[taskId];
  if (at === undefined) return false;
  return startOfDay(at) === startOfDay(now);
}

/** Drop all locally-planned tasks for the signed-in tenant (sign-out). */
export function clearPlannedToday(): void {
  try {
    localStorage.removeItem(storageKey());
  } catch {
    /* nothing to clear */
  }
}
